<template>
  <div>
    <!-- 编辑组件的内容会通过props传入并在父组件中展示 -->
  </div>
</template>

<script setup>
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { fillDefaultZeroFields, convertVisionFieldsInForm } from '@/utils/opticalDataUtil';
import { isAuthRedirecting } from '@/utils/authRedirect';
import { isBetaHistoryModel, normalizeBetaHistoryDuration, parseBetaHistoryDuration } from '@/utils/betaHistoryDuration';
// eslint-disable-next-line no-unused-vars
const props = defineProps({
  // 是否显示移动端样式
  isMobile: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'cancel']);

const buildDurationOptions = (type, selected) => {
  const max = type === 'months' ? 120 : 31;
  const options = ['<option value="">请选择</option>'];
  for (let value = 0; value <= max; value += 1) {
    options.push(`<option value="${value}" ${String(selected) === String(value) ? 'selected' : ''}>${value}</option>`);
  }
  return options.join('');
};

const getBetaHistoryUsedValue = name =>
  document.querySelector(`input[name="${name}"]:checked`)?.value || null;

const getDurationValue = (usedName, monthsId, daysId) => {
  if (getBetaHistoryUsedValue(usedName) !== '是') return null;
  const months = document.getElementById(monthsId)?.value;
  const days = document.getElementById(daysId)?.value;
  if (months === '' && days === '') return null;
  return `${months || 0}个月${days || 0}天`;
};

const formatDuration = value => normalizeBetaHistoryDuration(value) || value || '-';

const setupBetaHistoryForm = () => {
  const sections = [
    {
      usedName: 'edit-atropineEyeDropsUsed',
      durationId: 'edit-atropineEyeDropsDuration',
      monthsId: 'edit-atropineEyeDropsStopMonths',
      daysId: 'edit-atropineEyeDropsStopDays',
      elapsedId: 'edit-atropineEyeDropsElapsed',
      elapsedLabel: '已停药'
    },
    {
      usedName: 'edit-orthokeratologyLensUsed',
      durationId: 'edit-orthokeratologyLensDuration',
      monthsId: 'edit-orthokeratologyLensStopMonths',
      daysId: 'edit-orthokeratologyLensStopDays',
      elapsedId: 'edit-orthokeratologyLensElapsed',
      elapsedLabel: '已停戴'
    }
  ];

  sections.forEach(({ usedName, durationId, monthsId, daysId, elapsedId, elapsedLabel }) => {
    const duration = document.getElementById(durationId);
    const months = document.getElementById(monthsId);
    const days = document.getElementById(daysId);
    const elapsed = document.getElementById(elapsedId);
    if (!duration || !months || !days || !elapsed) return;

    const updateElapsed = () => {
      const isUsed = getBetaHistoryUsedValue(usedName) === '是';
      const hasDuration = months.value !== '' || days.value !== '';
      duration.style.display = isUsed ? 'grid' : 'none';

      if (!isUsed) {
        elapsed.hidden = true;
        return;
      }

      elapsed.hidden = !hasDuration;
      if (hasDuration) {
        elapsed.textContent = `${elapsedLabel} ${months.value || 0}个月${days.value || 0}天`;
      }
    };

    document.querySelectorAll(`input[name="${usedName}"]`).forEach(input => {
      input.addEventListener('change', updateElapsed);
    });
    months.addEventListener('change', updateElapsed);
    days.addEventListener('change', updateElapsed);
    updateElapsed();
  });
};


// 获取验配单位列表（用于编辑弹窗下拉）
const fetchOptometryClinicOptions = async () => {
  try {
    const response = await axios.get('/api/v1/inspection-management/all', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.data.code === '200' && response.data.data) {
      const clinics = response.data.data || [];
      const unique = [...new Set(clinics.map(item => item.optometryClinic).filter(Boolean))];
      return unique;
    }
  } catch (e) {
    if (e?.response?.status === 401 || isAuthRedirecting()) {
      return null;
    }
    console.error('获取验配单位列表失败', e);
  }
  return [];
};

const handleEdit = async (row) => {
  try {
    if (isAuthRedirecting()) {
      return;
    }

    // 计算当前行的序号
    const orderIndex = row.index;
    const displayId = orderIndex + 1 + (row.currentPage - 1) * row.pageSize;

    // 先获取验配单位列表供下拉使用
    const optometryClinicOptions = await fetchOptometryClinicOptions();
    if (optometryClinicOptions === null || isAuthRedirecting()) {
      return;
    }
    const currentCompany = (row.company || '').trim();
    const atropineDuration = parseBetaHistoryDuration(row.atropineEyeDropsStopDuration) || { months: '', days: '' };
    const orthokeratologyDuration = parseBetaHistoryDuration(row.orthokeratologyLensStopDuration) || { months: '', days: '' };
    const hasCurrentInList = currentCompany && optometryClinicOptions.includes(currentCompany);
    let companyOptionsHtml = optometryClinicOptions.map(c =>
      `<option value="${String(c || '').replace(/"/g, '&quot;')}" ${currentCompany === c ? 'selected' : ''}>${String(c || '').replace(/</g, '&lt;')}</option>`
    ).join('');
    if (currentCompany && !hasCurrentInList) {
      companyOptionsHtml = `<option value="${currentCompany.replace(/"/g, '&quot;')}" selected>${currentCompany.replace(/</g, '&lt;')}</option>` + companyOptionsHtml;
    }
    
    const confirmPromise = ElMessageBox.confirm(
        `<div class="edit-order-form">
        <!-- 第一部分：订单基本信息 -->
        <div class="form-section basic-info">
          <h3>订单基本信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>序号：</label>
              <input type="text" class="el-input__inner" value="${displayId}" readonly>
              <input type="hidden" id="edit-id" value="${row.id || ''}">
            </div>
            <div class="form-item">
              <label>订单号：</label>
              <input type="text" class="el-input__inner" value="${row.orderNumber || ''}" id="edit-orderNumber" readonly>
            </div>
            <div class="form-item">
              <label>用户ID：</label>
              <input type="text" class="el-input__inner" value="${row.userId || ''}" id="edit-userId" readonly>
            </div>
            <div class="form-item">
              <label>订单日期<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.orderDate || ''}" id="edit-orderDate" readonly>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>订单状态：</label>
              <select class="el-input__inner" id="edit-status">
                <option value="Exported" ${row.status === 'Exported' ? 'selected' : ''}>已导出</option>
                <option value="Noexported" ${row.status === 'Noexported' ? 'selected' : ''}>未导出</option>
              </select>
            </div>
            <div class="form-item">
              <label>订单类型：</label>
              <input type="text" class="el-input__inner" value="${row.orderType || '散订单'}" id="edit-orderType" readonly>
            </div>
            <div class="form-item">
              <label>患者姓名<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.patientName || ''}" id="edit-patientName" required>
            </div>
            <div class="form-item">
              <label>年龄<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.age || ''}" id="edit-age">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>验光师：</label>
              <input type="text" class="el-input__inner" value="${row.optometrist || ''}" id="edit-optometrist">
            </div>
            <div class="form-item">
              <label>地区：</label>
              <input type="text" class="el-input__inner" value="${row.region || ''}" id="edit-region">
            </div>
            <div class="form-item">
              <label>订单型号<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.lensModel || ''}" id="edit-lensModel">
            </div>
            <div class="form-item">
              <label>验配单位<span class="required-star">*</span>：</label>
              <select class="el-input__inner" id="edit-company">
                <option value="">请选择验配单位</option>
                ${companyOptionsHtml}
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>采购单位<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.purchasingUnits || ''}" id="edit-purchasingUnits">
            </div>
          </div>
        </div>
          <div class="form-section beta-history-section" style="display: ${isBetaHistoryModel(row.lensModel) ? 'block' : 'none'}">
            <h3>超主点β既往情况</h3>
            <div class="beta-history-grid">
              <div class="beta-history-item">
                <div class="beta-history-label">是否有使用阿托品滴眼液<span class="required-star">*</span></div>
                <div class="beta-history-radio" role="radiogroup" aria-label="是否有使用阿托品滴眼液">
                  <label><input type="radio" name="edit-atropineEyeDropsUsed" value="是" ${row.atropineEyeDropsUsed === '是' ? 'checked' : ''}> 是</label>
                  <label><input type="radio" name="edit-atropineEyeDropsUsed" value="否" ${row.atropineEyeDropsUsed === '否' ? 'checked' : ''}> 否</label>
                </div>
                <div class="beta-history-duration" id="edit-atropineEyeDropsDuration">
                  <div class="beta-history-sublabel">停药时长<span class="required-star">*</span></div>
                  <div class="beta-duration-control">
                    <select class="el-input__inner" id="edit-atropineEyeDropsStopMonths">${buildDurationOptions('months', atropineDuration.months)}</select>
                    <span class="beta-duration-unit">个月</span>
                    <select class="el-input__inner" id="edit-atropineEyeDropsStopDays">${buildDurationOptions('days', atropineDuration.days)}</select>
                    <span class="beta-duration-unit">天</span>
                    <span class="beta-history-elapsed" id="edit-atropineEyeDropsElapsed" ${atropineDuration.months === '' && atropineDuration.days === '' ? 'hidden' : ''}>已停药 ${row.atropineEyeDropsStopDuration || ''}</span>
                  </div>
                </div>
              </div>
              <div class="beta-history-item">
                <div class="beta-history-label">是否配戴过角膜塑形镜（OK镜）<span class="required-star">*</span></div>
                <div class="beta-history-radio" role="radiogroup" aria-label="是否配戴过角膜塑形镜">
                  <label><input type="radio" name="edit-orthokeratologyLensUsed" value="是" ${row.orthokeratologyLensUsed === '是' ? 'checked' : ''}> 是</label>
                  <label><input type="radio" name="edit-orthokeratologyLensUsed" value="否" ${row.orthokeratologyLensUsed === '否' ? 'checked' : ''}> 否</label>
                </div>
                <div class="beta-history-duration" id="edit-orthokeratologyLensDuration">
                  <div class="beta-history-sublabel">停戴时长<span class="required-star">*</span></div>
                  <div class="beta-duration-control">
                    <select class="el-input__inner" id="edit-orthokeratologyLensStopMonths">${buildDurationOptions('months', orthokeratologyDuration.months)}</select>
                    <span class="beta-duration-unit">个月</span>
                    <select class="el-input__inner" id="edit-orthokeratologyLensStopDays">${buildDurationOptions('days', orthokeratologyDuration.days)}</select>
                    <span class="beta-duration-unit">天</span>
                    <span class="beta-history-elapsed" id="edit-orthokeratologyLensElapsed" ${orthokeratologyDuration.months === '' && orthokeratologyDuration.days === '' ? 'hidden' : ''}>已停戴 ${row.orthokeratologyLensStopDuration || ''}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        <!-- 第二部分：常规验光和超主点验光 -->
        <div class="form-section two-columns">
          <!-- 常规验光 -->
          <div class="column regular-refraction-column">
            <h3>常规验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOdSphere || ''}" id="edit-regularOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOdCylinder || ''}" id="edit-regularOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOdAxis || ''}" id="edit-regularOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOdVision || ''}" id="edit-regularOdVision">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOsSphere || ''}" id="edit-regularOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOsCylinder || ''}" id="edit-regularOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.regularOsAxis || ''}" id="edit-regularOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner" value="${row.regularOsVision || ''}" id="edit-regularOsVision">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼融像：</label>
                <input type="text" class="el-input__inner" value="${row.regularBinocularFusion || ''}" id="edit-regularBinocularFusion">
              </div>
              <div class="form-item">
                <label>立体视功能：</label>
                <input type="text" class="el-input__inner" value="${row.regularStereoscopicVisionFunction || ''}" id="edit-regularStereoscopicVisionFunction">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner" value="${row.regularBinocularVision || ''}" id="edit-regularBinocularVision">
              </div>
            </div>
          </div>

          <!-- 散瞳验光 -->
          <div class="column cycloplegic-refraction-column">
            <h3>散瞳验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOdSphere || ''}" id="edit-cycloplegicOdSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOdCylinder || ''}" id="edit-cycloplegicOdCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOdAxis || ''}" id="edit-cycloplegicOdAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOdVision || ''}" id="edit-cycloplegicOdVision"></div></div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact"><div class="form-item"><label>球镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOsSphere || ''}" id="edit-cycloplegicOsSphere"></div><div class="form-item"><label>柱镜：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOsCylinder || ''}" id="edit-cycloplegicOsCylinder"></div></div>
                <div class="form-row compact"><div class="form-item"><label>轴位：</label><input type="text" class="el-input__inner numeric-input" value="${row.cycloplegicOsAxis || ''}" id="edit-cycloplegicOsAxis"></div><div class="form-item"><label>视力：</label><input type="text" class="el-input__inner" value="${row.cycloplegicOsVision || ''}" id="edit-cycloplegicOsVision"></div></div>
              </div>
            </div>
          </div>

          <!-- 超主点验光 -->
          <div class="column trial-refraction-column">
            <h3>超主点验光</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOdSphere || ''}" id="edit-superOdSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOdCylinder || ''}" id="edit-superOdCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOdAxis || ''}" id="edit-superOdAxis">
                  </div>
                  <div class="form-item">
                    <label>视力<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner" value="${row.superOdVision || ''}" id="edit-superOdVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odQuantity || ''}" id="edit-odQuantity">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>球镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOsSphere || ''}" id="edit-superOsSphere">
                  </div>
                  <div class="form-item">
                    <label>柱镜<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOsCylinder || ''}" id="edit-superOsCylinder">
                  </div>
                </div>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>轴位<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.superOsAxis || ''}" id="edit-superOsAxis">
                  </div>
                  <div class="form-item">
                    <label>视力<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner n" value="${row.superOsVision || ''}" id="edit-superOsVision">
                  </div>
                  <div class="form-item">
                    <label>数量：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osQuantity || ''}" id="edit-osQuantity">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼融像：</label>
                <input type="text" class="el-input__inner" value="${row.superBinocularFusion || ''}" id="edit-superBinocularFusion">
              </div>
              <div class="form-item">
                <label>立体视功能：</label>
                <input type="text" class="el-input__inner" value="${row.superStereoscopicVisionFunction || ''}" id="edit-superStereoscopicVisionFunction">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼视力：</label>
                <input type="text" class="el-input__inner " value="${row.superBinocularVision || ''}" id="edit-superBinocularVision">
              </div>
            </div>
          </div>
        </div>

        <!-- 第三部分：角膜曲率和眼镜参数 -->
        <div class="form-section two-columns">
          <!-- 角膜曲率 -->
          <div class="column">
            <h3>角膜曲率</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odK1 || ''}" id="edit-odK1">
                  </div>
                  <div class="form-item">
                    <label>K2<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odK2 || ''}" id="edit-odK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odAxialLength || ''}" id="edit-odAxialLength">
                  </div>
                  <div class="form-item">
                    <label>脉络膜厚度：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odChoroidalThickness || ''}" id="edit-odChoroidalThickness">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>K1<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osK1 || ''}" id="edit-osK1">
                  </div>
                  <div class="form-item">
                    <label>K2<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osK2 || ''}" id="edit-osK2">
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-item">
                    <label>眼轴<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osAxialLength || ''}" id="edit-osAxialLength">
                  </div>
                  <div class="form-item">
                    <label>脉络膜厚度：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osChoroidalThickness || ''}" id="edit-osChoroidalThickness">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 眼镜参数 -->
          <div class="column">
            <h3>眼镜参数</h3>
            <div class="eye-data-container">
              <div class="eye-section">
                <h4>右眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odPupilHeight || ''}" id="edit-odPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.odPupilDistance || ''}" id="edit-odPupilDistance">
                  </div>
                </div>
              </div>
              <div class="eye-section">
                <h4>左眼</h4>
                <div class="form-row compact">
                  <div class="form-item">
                    <label>瞳高<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osPupilHeight || ''}" id="edit-osPupilHeight">
                  </div>
                  <div class="form-item">
                    <label>瞳距<span class="required-star">*</span>：</label>
                    <input type="text" class="el-input__inner numeric-input" value="${row.osPupilDistance || ''}" id="edit-osPupilDistance">
                  </div>
                </div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>双眼瞳距：</label>
                <input type="text" class="el-input__inner numeric-input" value="${row.binocularPupilDistance || ''}" id="edit-binocularPupilDistance">
              </div>
            </div>
            <div class="form-row">
              <div class="form-item">
                <label>镜框编号：</label>
                <input type="text" class="el-input__inner" value="${row.frameNumber || ''}" id="edit-frameNumber">
              </div>
              <div class="form-item">
                <label>镜框类型：</label>
                <select class="el-input__inner" id="edit-frameType">
                  <option value="来框加工" ${row.frameType === '来框加工' ? 'selected' : ''}>来框加工</option>
                  <option value="豪赋镜框加工" ${row.frameType === '豪赋镜框加工' ? 'selected' : ''}>豪赋镜框加工</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- 第五部分：邮寄信息 -->
        <div class="form-section delivery-info">
          <h3>邮寄信息</h3>
          <div class="form-row">
            <div class="form-item">
              <label>收件人<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.addressee || ''}" id="edit-addressee">
            </div>
            <div class="form-item">
              <label>联系电话<span class="required-star">*</span>：</label>
              <input type="text" class="el-input__inner" value="${row.phone || ''}" id="edit-phone">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>物流编号：</label>
              <input type="text" class="el-input__inner" value="${row.trackingNumber || ''}" id="edit-trackingNumber">
            </div>
            <div class="form-item">
              <label>邮寄日期：</label>
              <input type="text" class="el-input__inner" value="${row.deliveryDate || ''}" id="edit-deliveryDate">
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>邮寄地址<span class="required-star">*</span>：</label>
              <textarea class="el-textarea__inner" id="edit-shippingAddress">${row.shippingAddress || ''}</textarea>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item full-width">
              <label>备注：</label>
              <textarea class="el-textarea__inner" id="edit-remarks">${row.remarks || ''}</textarea>
            </div>
          </div>
        </div>
      </div>`,
        '编辑订单',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          customClass: 'edit-order-dialog'
        }
    );

    setTimeout(setupBetaHistoryForm, 80);
    await confirmPromise;

    if (isAuthRedirecting()) {
      return;
    }

    // 添加事件监听器
    setTimeout(() => {

      // 为数字输入框添加验证
      import('@/utils/opticalDataUtil').then(({ validateNumericInput }) => {
        // 获取所有带有numeric-input类的输入框
        const numericInputs = document.querySelectorAll('.numeric-input');
        
        // 为每个数字输入框添加验证事件
        numericInputs.forEach(input => {
          const fieldName = input.previousElementSibling.textContent.replace('*：', '：').replace('：', '');
          
          // 输入时验证
          input.addEventListener('input', function() {
            this.value = validateNumericInput(this.value, fieldName, false);
          });
          
          // 失去焦点时格式化
          input.addEventListener('blur', function() {
            this.value = validateNumericInput(this.value, fieldName, true);
          });
        });
      });

      // 设置输入最大长度
      const maxlengthMap = {
        'edit-patientName': 30,
        'edit-age': 10,
        'edit-optometrist': 10,
        'edit-region': 55,
        'edit-lensModel': 30,
        'edit-company': 30,
        'edit-purchasingUnits': 30,
        'edit-frameNumber': 30,
        'edit-binocularFusion': 60,
        'edit-stereoscopicVisionFunction': 60,
        'edit-addressee': 10,
        'edit-phone': 15,
        'edit-regularOdVision': 55,
        'edit-regularOsVision': 55,
        'edit-regularBinocularVision': 55,
        'edit-superOdVision': 55,
        'edit-superOsVision': 55,
        'edit-superBinocularVision': 55
      };
      Object.entries(maxlengthMap).forEach(([id, len]) => {
        const el = document.getElementById(id);
        if (el) el.setAttribute('maxlength', len);
      });
    }, 100);

    // 获取所有表单数据
    const orderData = {
      id: document.getElementById('edit-id').value,
      userId: document.getElementById('edit-userId').value,
      orderType: document.getElementById('edit-orderType').value,
      orderNumber: document.getElementById('edit-orderNumber').value,
      status: document.getElementById('edit-status').value,
      orderDate: document.getElementById('edit-orderDate').value,
      patientName: document.getElementById('edit-patientName').value,
      age: document.getElementById('edit-age').value || null,
      optometrist: document.getElementById('edit-optometrist').value || null,
      region: document.getElementById('edit-region').value || null,
      lensModel: document.getElementById('edit-lensModel').value,
      company: document.getElementById('edit-company').value || null,
      purchasingUnits: document.getElementById('edit-purchasingUnits').value || null,
      atropineEyeDropsUsed: getBetaHistoryUsedValue('edit-atropineEyeDropsUsed'),
      atropineEyeDropsStopDuration: getDurationValue('edit-atropineEyeDropsUsed', 'edit-atropineEyeDropsStopMonths', 'edit-atropineEyeDropsStopDays'),
      orthokeratologyLensUsed: getBetaHistoryUsedValue('edit-orthokeratologyLensUsed'),
      orthokeratologyLensStopDuration: getDurationValue('edit-orthokeratologyLensUsed', 'edit-orthokeratologyLensStopMonths', 'edit-orthokeratologyLensStopDays'),

      // 常规验光 - 右眼
      regularOdSphere: document.getElementById('edit-regularOdSphere').value || null,
      regularOdCylinder: document.getElementById('edit-regularOdCylinder').value || null,
      regularOdAxis: document.getElementById('edit-regularOdAxis').value || null,
      regularOdVision: document.getElementById('edit-regularOdVision').value || null,
      // 常规验光 - 左眼
      regularOsSphere: document.getElementById('edit-regularOsSphere').value || null,
      regularOsCylinder: document.getElementById('edit-regularOsCylinder').value || null,
      regularOsAxis: document.getElementById('edit-regularOsAxis').value || null,
      regularOsVision: document.getElementById('edit-regularOsVision').value || null,
      regularBinocularVision: document.getElementById('edit-regularBinocularVision').value || null,
      regularBinocularFusion: document.getElementById('edit-regularBinocularFusion').value || null,
      regularStereoscopicVisionFunction: document.getElementById('edit-regularStereoscopicVisionFunction').value || null,
      // 散瞳验光
      cycloplegicOdSphere: document.getElementById('edit-cycloplegicOdSphere').value || null,
      cycloplegicOdCylinder: document.getElementById('edit-cycloplegicOdCylinder').value || null,
      cycloplegicOdAxis: document.getElementById('edit-cycloplegicOdAxis').value || null,
      cycloplegicOdVision: document.getElementById('edit-cycloplegicOdVision').value || null,
      cycloplegicOsSphere: document.getElementById('edit-cycloplegicOsSphere').value || null,
      cycloplegicOsCylinder: document.getElementById('edit-cycloplegicOsCylinder').value || null,
      cycloplegicOsAxis: document.getElementById('edit-cycloplegicOsAxis').value || null,
      cycloplegicOsVision: document.getElementById('edit-cycloplegicOsVision').value || null,
      // 超主点验光 - 右眼
      superOdSphere: document.getElementById('edit-superOdSphere').value || null,
      superOdCylinder: document.getElementById('edit-superOdCylinder').value || null,
      superOdAxis: document.getElementById('edit-superOdAxis').value || null,
      superOdVision: document.getElementById('edit-superOdVision').value || null,
      odQuantity: document.getElementById('edit-odQuantity').value || null,
      // 超主点验光 - 左眼
      superOsSphere: document.getElementById('edit-superOsSphere').value || null,
      superOsCylinder: document.getElementById('edit-superOsCylinder').value || null,
      superOsAxis: document.getElementById('edit-superOsAxis').value || null,
      superOsVision: document.getElementById('edit-superOsVision').value || null,
      osQuantity: document.getElementById('edit-osQuantity').value || null,
      superBinocularVision: document.getElementById('edit-superBinocularVision').value || null,
      superBinocularFusion: document.getElementById('edit-superBinocularFusion').value || null,
      superStereoscopicVisionFunction: document.getElementById('edit-superStereoscopicVisionFunction').value || null,
      // 角膜曲率 - 右眼
      odK1: document.getElementById('edit-odK1').value || null,
      odK2: document.getElementById('edit-odK2').value || null,
      odAxialLength: document.getElementById('edit-odAxialLength').value || null,
      odChoroidalThickness: document.getElementById('edit-odChoroidalThickness').value || null,
      // 角膜曲率 - 左眼
      osK1: document.getElementById('edit-osK1').value || null,
      osK2: document.getElementById('edit-osK2').value || null,
      osAxialLength: document.getElementById('edit-osAxialLength').value || null,
      osChoroidalThickness: document.getElementById('edit-osChoroidalThickness').value || null,
      // 眼镜参数 - 右眼
      odPupilHeight: document.getElementById('edit-odPupilHeight').value || null,
      odPupilDistance: document.getElementById('edit-odPupilDistance').value || null,
      // 眼镜参数 - 左眼
      osPupilHeight: document.getElementById('edit-osPupilHeight').value || null,
      osPupilDistance: document.getElementById('edit-osPupilDistance').value || null,
      binocularPupilDistance: document.getElementById('edit-binocularPupilDistance').value || null,
      // 镜框类型和编号
      frameNumber: document.getElementById('edit-frameNumber').value || null,
      frameType: document.getElementById('edit-frameType').value || null,
      // 邮寄信息
      addressee: document.getElementById('edit-addressee').value || null,
      phone: document.getElementById('edit-phone').value || null,
      trackingNumber: document.getElementById('edit-trackingNumber').value || null,
      deliveryDate: document.getElementById('edit-deliveryDate').value || null,
      shippingAddress: document.getElementById('edit-shippingAddress').value || null,
      remarks: document.getElementById('edit-remarks').value || null
    };
    const validateFields = (data, rules) => {
      for (const rule of rules) {
        if (!data[rule.field]) {
          ElMessage.error(rule.message);
          return false;
        }
      }
      return true;
    };

    // 定义验证规则
    const validationRules = [
      { field: 'patientName', message: '请输入患者姓名' },
      { field: 'lensModel', message: '请选择订单型号' },
      { field: 'company', message: '请输入验配单位' },
      { field: 'purchasingUnits', message: '请输入采购单位' },
      { field: 'age', message: '请输入年龄' },
      { field: 'regularOdSphere', message: '请输入常规验光右眼球镜' },
      { field: 'regularOdVision', message: '请输入常规验光右眼视力' },
      { field:'regularOdCylinder', message: '请输入常规验光右眼柱镜'},
      { field:'regularOdAxis', message: '请输入常规验光右眼轴位' },
      { field: 'regularOsSphere', message: '请输入常规验光左眼球镜' },
      { field: 'regularOsVision', message: '请输入常规验光左眼视力' },
      { field: 'regularOsAxis', message: '请输入常规验光左眼轴位'},
      { field:'regularOsCylinder', message: '请输入常规验光左眼柱镜'},
      { field: 'superOdSphere', message: '请输入超主点验光右眼球镜' },
      { field: 'superOdAxis', message: '请输入超主点验光右眼轴位'},
      { field:'superOdCylinder', message: '请输入超主点验光右眼柱镜'},
      { field: 'superOdVision', message: '请输入超主点验光右眼视力' },
      { field: 'superOsSphere', message: '请输入超主点验光左眼球镜' },
      { field: 'superOsAxis', message: '请输入超主点验光左眼轴位'},
      { field:'superOsCylinder', message: '请输入超主点验光左眼柱镜'},
      { field: 'superOsVision', message: '请输入超主点验光左眼视力' },
      { field: 'odK1', message: '请输入角膜曲率右眼K1' },
      { field: 'odK2', message: '请输入角膜曲率右眼K2' },
      { field: 'odAxialLength', message: '请输入角膜曲率右眼眼轴' },
      { field: 'osK1', message: '请输入角膜曲率左眼K1' },
      { field: 'osK2', message: '请输入角膜曲率左眼K2' },
      { field: 'osAxialLength', message: '请输入角膜曲率左眼眼轴' },
      { field: 'odPupilHeight', message: '请输入右眼瞳高' },
      { field: 'osPupilHeight', message: '请输入左眼瞳高' },
      { field: 'odPupilDistance', message: '请输入右眼瞳距' },
      { field: 'osPupilDistance', message: '请输入左眼瞳距' },
      { field:'addressee', message: '请输入收件人姓名'},
      { field: 'shippingAddress', message: '请输入邮寄地址' },
      { field: 'phone', message: '请输入联系电话'}
    ];

    // 默认值填充（柱镜/轴位为空补0.00）
    fillDefaultZeroFields(orderData);
    // 编辑保存前：对数视力 → 小数视力（方案B）
    convertVisionFieldsInForm(orderData);

    // 执行验证
    if (!validateFields(orderData, validationRules)) {
      return;
    }

    if (isBetaHistoryModel(orderData.lensModel)) {
      if (!['是', '否'].includes(orderData.atropineEyeDropsUsed)) {
        ElMessage.error('请选择是否有使用阿托品滴眼液');
        return;
      }
      if (orderData.atropineEyeDropsUsed === '是' && !orderData.atropineEyeDropsStopDuration) {
        ElMessage.error('请选择阿托品滴眼液停药时长');
        return;
      }
      if (!['是', '否'].includes(orderData.orthokeratologyLensUsed)) {
        ElMessage.error('请选择是否配戴过角膜塑形镜（OK镜）');
        return;
      }
      if (orderData.orthokeratologyLensUsed === '是' && !orderData.orthokeratologyLensStopDuration) {
        ElMessage.error('请选择角膜塑形镜（OK镜）停戴时长');
        return;
      }
    }

    // console.log('Sending order data:', orderData); // 添加日志

    const response = await axios.put(`/api/v1/admin/order`, orderData);

    if (response.data.code === '200') {
      ElMessage.success('修改成功');
      emit('update');
    } else {
      ElMessage.error(response.data.info || '修改失败');
    }
  } catch (error) {
    if (isAuthRedirecting()) {
      return;
    }
    if (error !== 'cancel') {
      ElMessage.error('修改失败');
    }
    emit('cancel');
  }
};

// 显示订单详情
const showOrderDetails = (row) => {
  // 将所有属性格式化为易读的表格
  let content = '<div class="order-details">';
  content += '<h3>订单基本信息</h3>';
  content += '<table class="details-table">';
  content += `<tr><td>订单号</td><td>${row.orderNumber || '-'}</td></tr>`;
  content += `<tr><td>订单类型</td><td>${row.orderType || '-'}</td></tr>`;
  content += `<tr><td>订单状态</td><td>${row.status === 'Exported' ? '已导出' : '未导出'}</td></tr>`;
  content += `<tr><td>患者姓名</td><td>${row.patientName || '-'}</td></tr>`;
  content += `<tr><td>年龄</td><td>${row.age || '-'}</td></tr>`;
  content += `<tr><td>验光师</td><td>${row.optometrist || '-'}</td></tr>`;
  content += `<tr><td>地区</td><td>${row.region || '-'}</td></tr>`;
  content += `<tr><td>订单型号</td><td>${row.lensModel || '-'}</td></tr>`;
  if (isBetaHistoryModel(row.lensModel)) {
    content += `<tr><td>是否使用阿托品滴眼液</td><td>${row.atropineEyeDropsUsed || '-'}</td></tr>`;
    content += `<tr><td>阿托品滴眼液停药时长</td><td>${formatDuration(row.atropineEyeDropsStopDuration)}</td></tr>`;
    content += `<tr><td>是否配戴过角膜塑形镜（OK镜）</td><td>${row.orthokeratologyLensUsed || '-'}</td></tr>`;
    content += `<tr><td>角膜塑形镜（OK镜）停戴时长</td><td>${formatDuration(row.orthokeratologyLensStopDuration)}</td></tr>`;
  }
  content += `<tr><td>单位</td><td>${row.company || '-'}</td></tr>`;
  content += `<tr><td>采购单位</td><td>${row.purchasingUnits || '-'}</td></tr>`;
  content += `<tr><td>创建时间</td><td>${row.createdAt || '-'}</td></tr>`;
  content += `<tr><td>更新时间</td><td>${row.updatedAt || '-'}</td></tr>`;
  content += '</table>';
  
  // 常规验光数据
  content += '<h3>常规验光</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>球镜</td><td>${row.regularOdSphere > 0 ? '+' + row.regularOdSphere : row.regularOdSphere || '-'}</td>
              <td>${row.regularOsSphere > 0 ? '+' + row.regularOsSphere : row.regularOsSphere || '-'}</td></tr>`;
  content += `<tr><td>柱镜</td><td>${row.regularOdCylinder || '-'}</td><td>${row.regularOsCylinder || '-'}</td></tr>`;
  content += `<tr><td>轴位</td><td>${row.regularOdAxis || '-'}</td><td>${row.regularOsAxis || '-'}</td></tr>`;
  content += `<tr><td>视力</td><td>${row.regularOdVision || '-'}</td><td>${row.regularOsVision || '-'}</td></tr>`;
  content += `<tr><td>双眼融像</td><td colspan="2">${row.regularBinocularFusion || '-'}</td></tr>`;
  content += `<tr><td>立体视功能</td><td colspan="2">${row.regularStereoscopicVisionFunction || '-'}</td></tr>`;
  content += `<tr><td>双眼视力</td><td colspan="2">${row.regularBinocularVision || '-'}</td></tr>`;
  content += '</table>';
  
  // 散瞳验光数据
  content += '<h3>散瞳验光</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>球镜</td><td>${row.cycloplegicOdSphere > 0 ? '+' + row.cycloplegicOdSphere : row.cycloplegicOdSphere || '-'}</td><td>${row.cycloplegicOsSphere > 0 ? '+' + row.cycloplegicOsSphere : row.cycloplegicOsSphere || '-'}</td></tr>`;
  content += `<tr><td>柱镜</td><td>${row.cycloplegicOdCylinder || '-'}</td><td>${row.cycloplegicOsCylinder || '-'}</td></tr>`;
  content += `<tr><td>轴位</td><td>${row.cycloplegicOdAxis || '-'}</td><td>${row.cycloplegicOsAxis || '-'}</td></tr>`;
  content += `<tr><td>视力</td><td>${row.cycloplegicOdVision || '-'}</td><td>${row.cycloplegicOsVision || '-'}</td></tr>`;
  content += '</table>';

  // 超主点验光数据
  content += '<h3>超主点验光</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>球镜</td><td>${row.superOdSphere > 0 ? '+' + row.superOdSphere : row.superOdSphere || '-'}</td>
              <td>${row.superOsSphere > 0 ? '+' + row.superOsSphere : row.superOsSphere || '-'}</td></tr>`;
  content += `<tr><td>柱镜</td><td>${row.superOdCylinder || '-'}</td><td>${row.superOsCylinder || '-'}</td></tr>`;
  content += `<tr><td>轴位</td><td>${row.superOdAxis || '-'}</td><td>${row.superOsAxis || '-'}</td></tr>`;
  content += `<tr><td>视力</td><td>${row.superOdVision || '-'}</td><td>${row.superOsVision || '-'}</td></tr>`;
  content += `<tr><td>数量</td><td>${row.odQuantity || '-'}</td><td>${row.osQuantity || '-'}</td></tr>`;
  content += `<tr><td>双眼融像</td><td colspan="2">${row.superBinocularFusion || '-'}</td></tr>`;
  content += `<tr><td>立体视功能</td><td colspan="2">${row.superStereoscopicVisionFunction || '-'}</td></tr>`;
  content += `<tr><td>双眼视力</td><td colspan="2">${row.superBinocularVision || '-'}</td></tr>`;
  content += '</table>';
  
  // 角膜曲率
  content += '<h3>角膜曲率</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>K1</td><td>${row.odK1 || '-'}</td><td>${row.osK1 || '-'}</td></tr>`;
  content += `<tr><td>K2</td><td>${row.odK2 || '-'}</td><td>${row.osK2 || '-'}</td></tr>`;
  content += `<tr><td>眼轴</td><td>${row.odAxialLength || '-'}</td><td>${row.osAxialLength || '-'}</td></tr>`;
  content += `<tr><td>脉络膜厚度</td><td>${row.odChoroidalThickness || '-'}</td><td>${row.osChoroidalThickness || '-'}</td></tr>`;
  content += '</table>';
  
  // 眼镜参数
  content += '<h3>眼镜参数</h3>';
  content += '<table class="details-table">';
  content += '<tr><th>参数</th><th>右眼</th><th>左眼</th></tr>';
  content += `<tr><td>瞳高</td><td>${row.odPupilHeight || '-'}</td><td>${row.osPupilHeight || '-'}</td></tr>`;
  content += `<tr><td>瞳距</td><td>${row.odPupilDistance || '-'}</td><td>${row.osPupilDistance || '-'}</td></tr>`;
  content += `<tr><td>双眼瞳距</td><td colspan="2">${row.binocularPupilDistance || '-'}</td></tr>`;
  content += `<tr><td>镜框编号</td><td colspan="2">${row.frameNumber || '-'}</td></tr>`;
  content += `<tr><td>镜框类型</td><td colspan="2">${row.frameType || '-'}</td></tr>`;
  content += '</table>';
  
  // 双眼视功能和邮寄信息
  content += '<h3>其他信息</h3>';
  content += '<table class="details-table">';
  content += `<tr><td>收件人</td><td>${row.addressee || '-'}</td></tr>`;
  content += `<tr><td>联系电话</td><td>${row.phone || '-'}</td></tr>`;
  content += `<tr><td>物流编号</td><td>${row.trackingNumber || '-'}</td></tr>`;
  content += `<tr><td>邮寄地址</td><td>${row.shippingAddress || '-'}</td></tr>`;
  content += `<tr><td>邮寄日期</td><td>${row.deliveryDate || '-'}</td></tr>`;
  content += `<tr><td>备注</td><td>${row.remarks || '-'}</td></tr>`;
  content += '</table>';
  
  content += '</div>';
  
  // 添加详情样式
  content += `<style>
    .el-message-box__container{
      display: block;
    }
    .order-details {
      font-size: 14px;
      padding: 0;
    }
    .order-details h3 {
      margin: 15px 0 8px;
      padding-bottom: 5px;
      border-bottom: 1px solid #EBEEF5;
      color: #409EFF;
      font-size: 16px;
    }
    .details-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
    }
    .details-table td, .details-table th {
      padding: 8px;
      border: 1px solid #EBEEF5;
      text-align: left;
    }
    .details-table th {
      background-color: #f5f7fa;
      color: #606266;
    }
    .el-message-box.details-dialog {
      width: 50% !important;
      max-width: 1200px !important;
    }
    .el-message-box.details-dialog .el-message-box__content {
      max-height: 70vh;
      overflow-y: auto;
    }
    @media screen and (max-width: 768px) {
      .order-details {
        font-size: 13px;
      }
      .details-table td, .details-table th {
        padding: 5px;
      }
    }
    @media screen and (max-width: 480px) {
      .el-message-box.details-dialog {
       width: 80% !important;
      }
    }
  </style>`;
  
  // 修改调用方式，添加catch来处理取消操作
  ElMessageBox.alert(content, '订单详情', {
    dangerouslyUseHTMLString: true,
    customClass: 'details-dialog',
    closeOnClickModal: true,
    distinguishCancelAndClose: true, // 区分取消和关闭操作
  }).catch(() => {
    // 静默处理取消或关闭操作
    console.log('对话框已关闭');
  });
};

// 导出Excel文件
const handleExportExcel = async () => {
  try {
    const response = await axios({
      url: '/api/v1/admin/excelexport',
      method: 'GET',
      responseType: 'json'
    });

    if (response.data.code === '200' && response.data.data) {
      const binaryString = window.atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      downloadElement.href = href;

      const date = new Date();
      const timestamp = date.getFullYear() +
          ('0' + (date.getMonth() + 1)).slice(-2) +
          ('0' + date.getDate()).slice(-2) +
          ('0' + date.getHours()).slice(-2) +
          ('0' + date.getMinutes()).slice(-2) +
          ('0' + date.getSeconds()).slice(-2);

      downloadElement.download = `超主点订单_${timestamp}.xlsx`;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);

      ElMessage.success('导出成功');
      emit('update');
      window.dispatchEvent(new CustomEvent('hotspot-refresh'));
    } else {
      ElMessage.error(response.data.info || '导出失败');
    }
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败');
  }
};

// 导出选中的订单
const handleExportSelectedOrders = async (selectedOrders) => {
  if (selectedOrders.length === 0) {
    ElMessage.warning('请至少选择一条订单');
    return;
  }
  
  // 检查是否包含已导出的订单
  const hasExportedOrders = selectedOrders.some(order => order.status === 'Exported');
  
  if (hasExportedOrders) {
    try {
      await ElMessageBox.confirm('选中的订单中包含已导出超主点的订单，是否继续导出？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });
      // 用户确认导出，继续执行
    } catch (e) {
      // 用户取消导出
      return;
    }
  }
  
  // 导出选中的订单
  try {
    const orderIds = selectedOrders.map(order => order.id);
    
    const response = await axios({
      url: '/api/v1/admin/exportSelectedOrders',
      method: 'POST',
      data: orderIds,
      responseType: 'json'
    });

    if (response.data.code === '200' && response.data.data) {
      const binaryString = window.atob(response.data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadElement = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      downloadElement.href = href;

      const date = new Date();
      const timestamp = date.getFullYear() +
          ('0' + (date.getMonth() + 1)).slice(-2) +
          ('0' + date.getDate()).slice(-2) +
          ('0' + date.getHours()).slice(-2) +
          ('0' + date.getMinutes()).slice(-2) +
          ('0' + date.getSeconds()).slice(-2);

      downloadElement.download = `超主点订单_${timestamp}.xlsx`;
      document.body.appendChild(downloadElement);
      downloadElement.click();
      document.body.removeChild(downloadElement);
      window.URL.revokeObjectURL(href);

      ElMessage.success('导出成功');
      emit('update');
      window.dispatchEvent(new CustomEvent('hotspot-refresh'));
    } else {
      ElMessage.error(response.data.info || '导出失败');
    }
  } catch (error) {
    console.error('导出失败:', error);
    ElMessage.error('导出失败');
  }
};

// 暴露函数给父组件使用
defineExpose({
  handleEdit,
  showOrderDetails,
  handleExportExcel,
  handleExportSelectedOrders
});
</script> 
